/**
 * Attributes for a payment method image
 */
interface PaymentMethodImageAttributes {
    src: string;
    title: string;
    alt: string;
    width: number;
    height: number;
}

/**
 * Default dimensions for payment method images
 */
const DEFAULT_IMAGE_SIZE = {width: 300, height: 112};

/**
 * Mapping of payment method types to their image attributes
 */
export const PAYMENT_METHOD_IMAGE_ATTRS: Record<string, PaymentMethodImageAttributes> = {
    amex: {src: 'card-amex.svg', title: 'AMEX', alt: 'AMEX', ...DEFAULT_IMAGE_SIZE},
    cartebancaire: {
        src: 'card-cartebancaire.svg',
        title: 'Cartebancaire',
        alt: 'Cartebancaire',
        ...DEFAULT_IMAGE_SIZE,
    },
    cup: {src: 'card-unionpay.svg', title: 'Cup', alt: 'Cup', ...DEFAULT_IMAGE_SIZE},
    unionpay: {src: 'card-unionpay.svg', title: 'Cup', alt: 'Cup', ...DEFAULT_IMAGE_SIZE},
    'default-method': {src: 'card-default.svg', title: '', alt: 'Card', ...DEFAULT_IMAGE_SIZE},
    diners: {
        src: 'card-dinersclub.svg',
        title: 'Diners Club',
        alt: 'Diners Club',
        ...DEFAULT_IMAGE_SIZE,
    },
    dinersclub: {
        src: 'card-dinersclub.svg',
        title: 'Diners Club',
        alt: 'Diners Club',
        ...DEFAULT_IMAGE_SIZE,
    },
    discover: {
        src: 'card-discover.svg',
        title: 'Discover',
        alt: 'DISCOVER',
        ...DEFAULT_IMAGE_SIZE,
    },
    elo: {src: 'card-elo.svg', title: 'Elo', alt: 'elo', ...DEFAULT_IMAGE_SIZE},
    hipercard: {src: 'card-hiper.svg', title: 'Hiper', alt: 'hiper', ...DEFAULT_IMAGE_SIZE},
    hiper: {src: 'card-hiper.svg', title: 'Hiper', alt: 'hiper', ...DEFAULT_IMAGE_SIZE},
    jcb: {src: 'card-jcb.svg', title: 'JCB', alt: 'JCB', ...DEFAULT_IMAGE_SIZE},
    maestro: {
        src: 'card-maestro.svg',
        title: 'Maestro',
        alt: 'Maestro',
        ...DEFAULT_IMAGE_SIZE,
    },
    mastercard: {
        src: 'card-mastercard.svg',
        title: 'MasterCard',
        alt: 'MasterCard',
        ...DEFAULT_IMAGE_SIZE,
    },
    mc: {
        src: 'card-mastercard.svg',
        title: 'MasterCard',
        alt: 'MasterCard',
        ...DEFAULT_IMAGE_SIZE,
    },
    rupay: {src: 'card-rupay.svg', title: 'Rupay', alt: 'rupay', ...DEFAULT_IMAGE_SIZE},
    troy: {src: 'card-troy.svg', title: 'Troy', alt: 'troy', ...DEFAULT_IMAGE_SIZE},
    visa: {src: 'card-visa.svg', title: 'Visa', alt: 'VISA', ...DEFAULT_IMAGE_SIZE},
    paytm: {src: 'hpp-paytm.svg', title: 'Paytm', alt: 'VISA', ...DEFAULT_IMAGE_SIZE},
    billdesk_upi: {src: 'hpp-upi.svg', title: 'Upi', alt: 'VISA', ...DEFAULT_IMAGE_SIZE},
    upi: {src: 'common-upi.svg', title: 'Upi', alt: 'VISA', ...DEFAULT_IMAGE_SIZE},
    'express-checkout': {
        src: 'hpp-paypal.svg',
        title: 'Paypal',
        alt: 'Paypal',
        ...DEFAULT_IMAGE_SIZE,
    },
    paypal_over_stripe: {
        src: 'hpp-paypal.svg',
        title: 'Paypal',
        alt: 'Paypal',
        ...DEFAULT_IMAGE_SIZE,
    },
    kcp_naverpay: {
        src: 'hpp-naverpay.svg',
        title: 'NaverPay',
        alt: 'NaverPay',
        ...DEFAULT_IMAGE_SIZE,
    },
    kcp_banktransfer: {
        src: 'hpp-billdesk-online.svg',
        title: 'KCP Bank Transfer',
        alt: 'KCP Bank Transfer',
        ...DEFAULT_IMAGE_SIZE,
    },
    bc: {src: 'card-bc.svg', title: 'BC', alt: 'BC', ...DEFAULT_IMAGE_SIZE},
    kb: {src: 'card-kb.svg', title: 'KB', alt: 'KB', ...DEFAULT_IMAGE_SIZE},
    lotte: {src: 'card-lotte.svg', title: 'Lotte', alt: 'Lotte', ...DEFAULT_IMAGE_SIZE},
    samsung: {src: 'card-samsung.svg', title: 'Samsung', alt: 'Samsung', ...DEFAULT_IMAGE_SIZE},
    shinhan: {src: 'card-shinhan.svg', title: 'Shinhan', alt: 'Shinhan', ...DEFAULT_IMAGE_SIZE},
    klarna: {
        src: 'hpp-klarna.svg',
        title: 'Klarna',
        alt: 'Klarna',
        ...DEFAULT_IMAGE_SIZE,
    },
    afterpay: {
        src: 'hpp-afterpay.svg',
        title: 'Afterpay',
        alt: 'Afterpay',
        ...DEFAULT_IMAGE_SIZE,
    },
    billdesk_online: {
        src: 'hpp-billdesk-online.svg',
        title: 'Net Banking',
        alt: 'Net Banking',
        ...DEFAULT_IMAGE_SIZE,
    },
    onlinebanking_IN: {
        src: 'common-onlinebanking-in.svg',
        title: 'Net Banking',
        alt: 'Net Banking',
        ...DEFAULT_IMAGE_SIZE,
    },
    billdesk_wallet: {
        src: 'hpp-billdesk-wallet.svg',
        title: 'Mobile Wallets',
        alt: 'Mobile Wallets',
        ...DEFAULT_IMAGE_SIZE,
    },
    wallet_IN: {
        src: 'common-wallet-in.svg',
        title: 'Mobile Wallets',
        alt: 'Mobile Wallets',
        ...DEFAULT_IMAGE_SIZE,
    },
    doku_alfamart: {
        src: 'hpp-doku-alfamart.svg',
        title: 'Alfamart',
        alt: 'Alfamart',
        ...DEFAULT_IMAGE_SIZE,
    },
    doku_bni_va: {
        src: 'hpp-doku-bni-va.svg',
        title: 'BNI',
        alt: 'BNI',
        ...DEFAULT_IMAGE_SIZE,
    },
    doku_bri_va: {
        src: 'hpp-doku-bri-va.svg',
        title: 'BRI',
        alt: 'BRI',
        ...DEFAULT_IMAGE_SIZE,
    },
    doku_cimb_va: {
        src: 'hpp-doku-cimb-va.svg',
        title: 'CIMB Niaga',
        alt: 'CIMB Niaga',
        ...DEFAULT_IMAGE_SIZE,
    },
    doku_danamon_va: {
        src: 'hpp-doku-danamon-va.svg',
        title: 'Bank Danamon',
        alt: 'Bank Danamon',
        ...DEFAULT_IMAGE_SIZE,
    },
    doku_indomaret: {
        src: 'hpp-doku-indomaret.svg',
        title: 'Indomaret',
        alt: 'Indomaret',
        ...DEFAULT_IMAGE_SIZE,
    },
    doku_mandiri_va: {
        src: 'hpp-doku-mandiri-va.svg',
        title: 'Bank Mandiri',
        alt: 'Bank Mandiri',
        ...DEFAULT_IMAGE_SIZE,
    },
    doku_wallet: {
        src: 'hpp-doku-wallet.svg',
        title: 'Doku Wallet',
        alt: 'Doku Wallet',
        ...DEFAULT_IMAGE_SIZE,
    },
    doku_ovo: {
        src: 'hpp-doku-ovo.svg',
        title: 'Ovo',
        alt: 'Ovo',
        ...DEFAULT_IMAGE_SIZE,
    },
    primeiropay_boleto: {
        src: 'hpp-primeiropay-boleto.svg',
        title: 'Boleto',
        alt: 'Boleto',
        ...DEFAULT_IMAGE_SIZE,
    },
    pix: {
        src: 'hpp-pix.svg',
        title: 'Pix',
        alt: 'Pix',
        ...DEFAULT_IMAGE_SIZE,
    },
    bcmc: {
        src: 'hpp-bancontact.svg',
        title: 'Bancontact Card',
        alt: 'Bancontact Card',
        ...DEFAULT_IMAGE_SIZE,
    },
    bcmc_mobile: {
        src: 'hpp-bancontact.svg',
        title: 'Payconiq by Bancontact',
        alt: 'Payconiq by Bancontact',
        ...DEFAULT_IMAGE_SIZE,
    },
    grabpay_SG: {
        src: 'hpp-grabpay.svg',
        title: 'GrabPay',
        alt: 'GrabPay',
        ...DEFAULT_IMAGE_SIZE,
    },
    grabpay_PH: {
        src: 'hpp-grabpay.svg',
        title: 'GrabPay',
        alt: 'GrabPay',
        ...DEFAULT_IMAGE_SIZE,
    },
    grabpay_MY: {
        src: 'hpp-grabpay.svg',
        title: 'GrabPay',
        alt: 'GrabPay',
        ...DEFAULT_IMAGE_SIZE,
    },
    onlineBanking_PL: {
        src: 'common-onlinebanking-pl.svg',
        title: 'Online Banking',
        alt: 'Online Banking',
        ...DEFAULT_IMAGE_SIZE,
    },
    oxxo: {
        src: 'hpp-oxxo.svg',
        title: 'OXXO',
        alt: 'OXXO',
        ...DEFAULT_IMAGE_SIZE,
    },
    verve: {
        src: 'card-verve.svg',
        title: 'Verve',
        alt: 'Verve',
        ...DEFAULT_IMAGE_SIZE,
    },
    apple_pay: {
        src: 'common-apple-pay.svg',
        title: 'Apple Pay',
        alt: 'Apple Pay',
        ...DEFAULT_IMAGE_SIZE,
    },
    google_pay: {
        src: 'common-google-pay.svg',
        title: 'Google Pay',
        alt: 'Google Pay',
        ...DEFAULT_IMAGE_SIZE,
    },
    mercado_pago: {
        src: 'hpp-mercado-pago.svg',
        title: 'Mercado Pago',
        alt: 'Mercado Pago',
        ...DEFAULT_IMAGE_SIZE,
    },
    bank_transfer: {
        src: 'hpp-bank-transfer.svg',
        title: 'Bank Transfer',
        alt: 'Bank Transfer',
        ...DEFAULT_IMAGE_SIZE,
    },
};

/**
 * Base folder path for payment method images
 */
export const PAYMENT_METHOD_IMAGE_FOLDER = '/staticx/udemy/images/v9/';
